import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getProjects, getFinancialLaunches } from '../utils/api';
import { FolderKanban, Receipt, Clock, CheckCircle2, DollarSign, Hammer, Building2, AlertTriangle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminDashboard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';

  const [metrics, setMetrics] = useState(null);
  const [recentLaunches, setRecentLaunches] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [metricsRes, projectsRes, launchesRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/metrics`, { headers: { Authorization: `Bearer ${token}` } }),
        getProjects(),
        getFinancialLaunches()
      ]);
      setMetrics(metricsRes.data);
      setProjects(projectsRes.data);
      setRecentLaunches(launchesRes.data.slice(0, 5));
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-emerald-600 bg-emerald-50">
          <CheckCircle2 className="h-3 w-3" /> Pago
        </span>
      );
    }
    if (status === 'submitted_receipt') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-blue-600 bg-blue-50">
          <Clock className="h-3 w-3" /> Comprovante Enviado
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-medium text-amber-600 bg-amber-50">
        <Clock className="h-3 w-3" /> Pendente
      </span>
    );
  };

  const getProjectStatusLabel = (status) => ({
    planned: 'Planejado', in_progress: 'Em Andamento', paused: 'Pausado', completed: 'Concluído'
  }[status] || status);

  const getProjectStatusColor = (status) => ({
    planned: 'bg-blue-50 text-blue-600',
    in_progress: 'bg-emerald-50 text-emerald-600',
    paused: 'bg-amber-50 text-amber-600',
    completed: 'bg-slate-100 text-slate-600'
  }[status] || 'bg-slate-100 text-slate-600');

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-secondary font-bold uppercase tracking-tight text-slate-900">
          Dashboard
        </h1>
        <p className="text-slate-600 font-primary mt-1">
          {isAdmin ? 'Visão geral do sistema' : 'Visão geral dos seus projetos'}
        </p>
      </div>

      {/* Top-row mini cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dashboard-summary-cards">
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-active-projects">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <FolderKanban className="h-4 w-4 text-blue-600" /> Projetos Ativos
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{metrics?.active_projects_count ?? '—'}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-active-empreitas">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <Hammer className="h-4 w-4 text-amber-600" /> Empreitas Ativas
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-2">{metrics?.active_empreitas_count ?? '—'}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-total-paid">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Total Pago
          </div>
          <p className="text-xl font-bold text-emerald-700 mt-2 tabular-nums">{fmtMoney(metrics?.total_paid)}</p>
          <div className="mt-2 space-y-0.5 text-xs text-slate-500">
            <p>Construtora: <span className="tabular-nums">{fmtMoney(metrics?.paid_construtora)}</span></p>
            <p>Materiais: <span className="tabular-nums">{fmtMoney(metrics?.paid_material)}</span></p>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4" data-testid="card-pending">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-secondary">
            <Clock className="h-4 w-4 text-amber-600" /> Pendentes ({metrics?.pending_count ?? 0})
          </div>
          <p className="text-xl font-bold text-amber-700 mt-2 tabular-nums">{fmtMoney(metrics?.pending_total)}</p>
          <div className="mt-2 space-y-0.5 text-xs text-slate-500">
            <p>Construtora ({metrics?.pending_construtora_count ?? 0}): <span className="tabular-nums">{fmtMoney(metrics?.pending_construtora_total)}</span></p>
            <p>Materiais ({metrics?.pending_material_count ?? 0}): <span className="tabular-nums">{fmtMoney(metrics?.pending_material_total)}</span></p>
          </div>
        </div>
      </div>

      {/* Saldo Devedor à Construtora */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-sm p-6 shadow-md" data-testid="card-saldo-devedor">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/10 rounded-sm">
              <Building2 className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-white/60 font-secondary">
                {isAdmin ? 'Saldo Devedor do Cliente à Construtora' : 'Saldo Devedor à Construtora'}
              </p>
              <p className="text-xs text-white/50 mt-0.5">Σ empreitas - já pago - pendente lançado</p>
            </div>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-3xl font-bold tabular-nums">{fmtMoney(metrics?.saldo_devedor_construtora)}</p>
            <p className="text-xs text-white/60 mt-1">de {fmtMoney(metrics?.total_empreitas_value)} contratado</p>
          </div>
        </div>
        {(metrics?.saldo_devedor_construtora || 0) < 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-300">
            <AlertTriangle className="h-3 w-3" /> Atenção: lançamentos já excedem o valor contratado de empreitas.
          </div>
        )}
      </div>

      {/* Two column layout for projects and launches */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Projects */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900">
              {isAdmin ? 'Projetos' : 'Meus Projetos'}
            </h2>
            <a href="/admin/projects" className="text-sm text-accent hover:underline">Ver todos</a>
          </div>
          <div className="space-y-3">
            {projects.slice(0, 5).map((project) => (
              <div key={project.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-sm hover:border-slate-200 transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-sm bg-accent/10 shrink-0">
                    <FolderKanban className="h-4 w-4 text-accent" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{project.name}</p>
                    {project.internal_code && <p className="text-xs text-slate-500">{project.internal_code}</p>}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-sm text-xs font-medium shrink-0 ${getProjectStatusColor(project.status)}`}>
                  {getProjectStatusLabel(project.status)}
                </span>
              </div>
            ))}
            {projects.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Nenhum projeto encontrado</p>}
          </div>
        </div>

        {/* Recent Launches */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900">
              {isAdmin ? 'Lançamentos Recentes' : 'Meus Lançamentos'}
            </h2>
            <a href="/admin/financial-launches" className="text-sm text-accent hover:underline">Ver todos</a>
          </div>
          <div className="space-y-3">
            {recentLaunches.map((launch) => (
              <div key={launch.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-sm hover:border-slate-200 transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-sm bg-purple-50 shrink-0">
                    <Receipt className="h-4 w-4 text-purple-600" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 text-sm line-clamp-1">
                      {launch.empreita_nome || launch.service_description || launch.favorecido || '—'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {fmtMoney(launch.amount)} • {new Date(launch.launch_date).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
                {getStatusBadge(launch.status)}
              </div>
            ))}
            {recentLaunches.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Nenhum lançamento encontrado</p>}
          </div>
        </div>
      </div>

      {/* Quick Actions - Only for Admin */}
      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
          <h2 className="text-lg font-secondary font-bold uppercase tracking-tight text-slate-900 mb-4">Ações Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/admin/projects" className="p-4 border border-slate-200 rounded-sm hover:border-slate-300 hover:shadow-md transition-all cursor-pointer" data-testid="quick-action-projects">
              <FolderKanban className="h-5 w-5 text-accent mb-2" strokeWidth={1.5} />
              <p className="font-medium text-slate-900">Gerenciar Projetos</p>
              <p className="text-xs text-slate-600 mt-1">Criar e editar projetos</p>
            </a>
            <a href="/admin/providers" className="p-4 border border-slate-200 rounded-sm hover:border-slate-300 hover:shadow-md transition-all cursor-pointer" data-testid="quick-action-providers">
              <Receipt className="h-5 w-5 text-accent mb-2" strokeWidth={1.5} />
              <p className="font-medium text-slate-900">Prestadores</p>
              <p className="text-xs text-slate-600 mt-1">Gerenciar fornecedores</p>
            </a>
            <a href="/admin/financial-launches" className="p-4 border border-slate-200 rounded-sm hover:border-slate-300 hover:shadow-md transition-all cursor-pointer" data-testid="quick-action-launches">
              <DollarSign className="h-5 w-5 text-accent mb-2" strokeWidth={1.5} />
              <p className="font-medium text-slate-900">Lançamentos</p>
              <p className="text-xs text-slate-600 mt-1">Criar novos lançamentos</p>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
